import React, { useContext, useState } from 'react';
import { Button, Col, Form, InputNumber, message, Row, Select, Table, Typography, Upload } from 'antd';

import { UploadChangeParam, UploadProps } from 'antd/lib/upload';
import { useForm } from 'antd/lib/form/Form';
import { UploadFile } from 'antd/lib/upload/interface';
import { CheckCircleFilled, LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { apiURL } from '../../../api/api';
import AuthContext from '../../../contexts/AuthContext';
import { getMonthName } from '../../../helpers/functions';
import { createPrepayment } from '../../../api/prepayments';
import { formatMoney } from '../../../utils/formatMoney';
import { IPrepaymentRow } from '../../../types/prepayments';

const PrepaymentsTable: React.FC = () => {
  const { adminRole } = useContext(AuthContext);
  const { writeAccess } = adminRole;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [preparedData, setPreparedData] = useState<IPrepaymentRow[] | null>(null);

  const now = new Date();
  const months = Array.from({ length: 12 }, (v, k) => k);
  
  const [form] = useForm();

  const handleUploadChange: UploadProps['onChange'] = (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === 'uploading') {
      setLoading(true);
      return;
    }
    if (info.file.status === 'done') {
	  setLoading(false);
	  setPreparedData(info.file.response.data);
    }
  };

  const uploadButton = (
    <div style={{ width: '100%' }}>
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>Завантажити звіт</div>
    </div>
  );

  const columns = [
	{
		title: '№ п/п',
		dataIndex: 'index',
		key: 'index',
		render: (_, { index }) => (
		  <Typography.Text>
			{index + 1}
		  </Typography.Text>
		),
	  },
	{
	  title: 'ID працівника',
	  dataIndex: 'employeeId',
	  key: 'employeeId',
	  render: (_, { employeeId }) => (
		<Typography.Text style={{ color: employeeId === null ? 'red' : 'black' }}>
		  {employeeId || 'Не знайдено'}
		</Typography.Text>
	  ),
	},
	{
	  title: 'Повне ім`я',
	  dataIndex: 'fullName',
	  key: 'fullName',
	  render: (_, { fullName }) => (
		<Typography.Text>
		  {fullName}
		</Typography.Text>
	  ),
	},
	{
	  title: 'Ідентифікаційний код',
	  dataIndex: 'identificationCode',
	  key: 'identificationCode',
	  render: (_, { identificationCode }) => (
		<Typography.Text>
		  {identificationCode}
		</Typography.Text>
	  ),
	},
	{
	  title: 'Сума авансу',
	  dataIndex: 'payment',
	  key: 'payment',
	  render: (_, { payment }) => (
		<Typography.Text>
		  {payment && formatMoney(payment)}
		</Typography.Text>
	  ),
	}
  ];

  const onConfirmSubmit = async ({ year, month, prepaymentType }) => {
	if (!preparedData) return;
	setLoading(true);
	setSuccess(false);

	try {
		await createPrepayment(year, month, prepaymentType, preparedData);
		message.success('Авансові платежі успішно створені');
		setSuccess(true);
	} catch (err) {
		console.error(err);
		message.error('Не вдалось створити авансові платежі: ' + (err as Error)?.message);
	} finally {
		setLoading(false);
	}
  };

  return (
	<div className="Prepayments">
	  <Row justify="center">
		<Col sm={24} md={12}>
			{!preparedData && <Row justify="center">
				<Col style={{ flexBasis: '50%' }}>
					<Upload
						name="file"
						listType="picture-card"
						className="avatar-uploader full-width"
						showUploadList={false}
						onChange={handleUploadChange}
						action={`${apiURL}/prepayments/prepare`}
					>
						{uploadButton}
					</Upload>
				</Col>
			</Row>}
			{ preparedData && writeAccess && !success &&
				<div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
					<Form
						form={form}
						layout="inline"
						onFinish={onConfirmSubmit as any}
						labelCol={{ span: 12 }}
						labelAlign="right"
						initialValues={{
							month: now.getMonth(),
							year: now.getFullYear()
						}}
						style={{ gap: '12px' }}
					>
						<Form.Item
							name="month"
							label="Місяць"
							rules={[{ required: true, message: 'Необхідно обрати місяць!' }]}
						>
							<Select placeholder="Оберіть місяць">
								{months.map(month => (
									<Select.Option value={month} key={month}>
									{getMonthName(month)}
									</Select.Option>
								))}
							</Select>
						</Form.Item>

						<Form.Item
							name="year"
							label="Рік"
							rules={[{ required: true, message: 'Необхідно ввести рік!' }]}
						>
							<InputNumber placeholder="Введіть рік" style={{ width: 120 }} min={2019} />
						</Form.Item>

						
						<Form.Item
							name="prepaymentType"
							label="Тип авансу"
							rules={[{ required: true, message: 'Необхідно обрати тип авансу!' }]}
						>
							<Select placeholder="Оберіть тип авансу">
								<Select.Option value="1">Аванс 1</Select.Option>
								<Select.Option value="2">Аванс 2</Select.Option>
							</Select>
						</Form.Item>

						<Button
							type="primary"
							icon={<CheckCircleFilled/>}
							onClick={() => form.submit()}
							style={{ marginLeft: '24px' }}
						>
							Завантажити
						</Button>
					</Form>
				</div>}
			{ preparedData && 
				<Table
					pagination={{ position: ['bottomCenter', 'topCenter'], pageSize: 20 }}
					loading={loading}
					//@ts-ignore
					columns={columns}
					dataSource={preparedData || []}
					rowKey="index"
					locale={{ emptyText: 'Нічого не знайдено' }}
				/>}
		</Col>
	  </Row>
	</div>
  );
}

export default PrepaymentsTable;
